import { NavLink } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import {
  Drawer,
  Typography,
  Avatar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Switch,
  ListItem,
  ListSubheader,
  Divider,
  Box,
  Tooltip,
} from '@mui/material'
import { DarkMode, Logout, Feedback } from '@mui/icons-material'
import { StyledStack, BackgroundAvatar } from './styles'
import { useCallback } from 'react'
import { useStores } from '../../app/providers/withStore'
import StartTourButton from '../../features/organization/map-picker/ui/StartTourButton'

const secondaryItems = [
  {
    href: '/feedback',
    icon: Feedback,
    title: 'Обратная связь',
  },
  {
    href: '/',
    icon: Logout,
    title: 'Выход',
  },
]

const activeLinkStyle = {
  backgroundColor: 'action.selected',
  '& .MuiListItemText-primary': {
    fontWeight: 'fontWeightMedium',
    color: 'primary.main',
  },
  '& .MuiListItemIcon-root': {
    color: 'primary.main',
  },
}

const SideBar = observer((props) => {
  const { drawerWidth, mobileOpen, handleDrawerToggle } = props
  const { authStore, organizationStore, themeStore, layoutStore } = useStores()
  const { isMini } = layoutStore

  const mainItems = layoutStore.navItems

  const handleThemeChange = useCallback(() => {
    themeStore.toggleTheme()
  }, [themeStore])

  const handleLogout = useCallback(() => {
    authStore.logout()
  }, [authStore])

  const drawerContent = (
    <>
      <StyledStack
        isMini={isMini}
        direction="column"
        sx={{
          alignItems: isMini ? 'center' : 'flex-start',
          px: isMini ? 2 : 4,
          transition: (theme) =>
            theme.transitions.create([
              'align-items',
              'padding-left',
              'padding-right',
            ]),
        }}
      >
        <BackgroundAvatar>
          <Avatar
            sx={{
              width: (theme) => theme.spacing(12),
              height: (theme) => theme.spacing(12),
            }}
            src={organizationStore.organization.imageUrl}
          />
        </BackgroundAvatar>
        {!isMini && (
          <>
            <Typography variant="h5">
              {organizationStore.organization.name}
            </Typography>
            <Typography>{authStore.currentUser?.email}</Typography>
          </>
        )}
      </StyledStack>
      <Stack
        sx={{ flexGrow: 1, pt: 2, overflowY: 'auto', overflowX: 'hidden' }}
      >
        <List
          component="nav"
          subheader={
            !isMini && (
              <ListSubheader
                sx={{ bgcolor: 'transparent', ml: 4, position: 'static' }}
              >
                Основные
              </ListSubheader>
            )
          }
          sx={{ mx: isMini ? 1 : 4, transition: 'margin 0.2s' }}
        >
          {mainItems.map((item) => (
            <Tooltip
              title={isMini ? item.text : ''}
              placement="right"
              key={item.text}
            >
              <ListItemButton
                component={NavLink}
                to={item.path}
                id={item.tourId} // Добавляем ID для продуктового тура
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  '&.active': activeLinkStyle,
                  justifyContent: isMini ? 'center' : 'flex-start',
                }}
              >
                <ListItemIcon
                  sx={{
                    color: 'primary.main',
                    minWidth: 0,
                    mr: isMini ? 0 : 2,
                    justifyContent: 'center',
                  }}
                >
                  <item.icon />
                </ListItemIcon>
                {!isMini && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          ))}
        </List>
        <Divider sx={{ mx: 4, mt: 2, mb: 2 }} />
        <List
          component="nav"
          subheader={
            !isMini && (
              <ListSubheader
                sx={{ bgcolor: 'transparent', ml: 4, position: 'static' }}
              >
                Прочее
              </ListSubheader>
            )
          }
          sx={{ mx: isMini ? 1 : 4, transition: 'margin 0.2s' }}
        >
          {isMini ? (
            <Tooltip title="Сменить тему" placement="right">
              <ListItemButton
                onClick={handleThemeChange}
                sx={{
                  justifyContent: 'center',
                  borderRadius: 2,
                  mb: 1,
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, color: 'primary.main' }}>
                  <DarkMode />
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          ) : (
            <ListItem sx={{ px: 1.5 }}>
              <ListItemIcon
                sx={{
                  color: 'primary.main',
                  minWidth: 0,
                  mr: 2,
                }}
              >
                <DarkMode />
              </ListItemIcon>
              <ListItemText primary="Тёмная тема" />
              <Switch
                checked={themeStore.mode === 'dark'}
                onChange={handleThemeChange}
                edge="end"
              />
            </ListItem>
          )}
          {secondaryItems.map((item) => (
            <Tooltip
              title={isMini ? item.title : ''}
              placement="right"
              key={item.title}
            >
              <ListItemButton
                component={item.title === 'Выход' ? 'div' : NavLink}
                to={item.title !== 'Выход' ? item.href : undefined}
                onClick={item.title === 'Выход' ? handleLogout : undefined}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  '&.active': activeLinkStyle,
                  justifyContent: isMini ? 'center' : 'flex-start',
                }}
              >
                <ListItemIcon
                  sx={{
                    color: 'text.secondary',
                    minWidth: 0,
                    mr: isMini ? 0 : 2,
                    justifyContent: 'center',
                  }}
                >
                  <item.icon />
                </ListItemIcon>
                {!isMini && (
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                      color: 'text.secondary',
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          ))}
        </List>
      </Stack>
    </>
  )

  return (
    <Box
      component="nav"
      sx={{
        width: { md: drawerWidth },
        flexShrink: { md: 0 },
        transition: (theme) => theme.transitions.create('width'),
      }}
    >
      {/* This is the mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            borderRadius: '0 80px 0 0',
            borderRight: 'none',
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            overflowX: 'hidden',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* This is the desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            borderRadius: '0 80px 0 0',
            borderRight: 'none',
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  )
})

export default SideBar
